/**
 * LiveKit participant mapping utilities.
 */
import {
  Participant,
  LocalParticipant,
  RemoteParticipant,
  Track,
  TrackPublication,
} from 'livekit-client';
import type { ParticipantData } from '../../types/meeting';

/**
 * Maps a LiveKit participant to the app's participant model.
 */
export function mapLiveKitParticipant(
  participant: Participant,
  isLocal: boolean
): ParticipantData {
  const audioTrack = getTrack(participant, Track.Source.Microphone);
  const metadata = parseParticipantMetadata(participant);
  const cameraOverride = resolveMetadataOptionalFlag(metadata, 'cameraOn');
  const cameraPublication = getCameraPublication(participant);
  const isVrSelfie = isVrSelfieTrackName(cameraPublication?.trackName);
  const trackCameraOn = isTrackEnabled(participant, Track.Source.Camera);
  const isCameraOn = isVrSelfie
    ? trackCameraOn
    : cameraOverride === undefined
      ? trackCameraOn
      : cameraOverride && trackCameraOn;
  const isSharingScreen = isTrackEnabled(participant, Track.Source.ScreenShare);
  const videoTrack = isCameraOn ? getCameraTrack(participant) : null;
  const displayName =
    resolveMetadataValue(metadata, 'displayName') ||
    participant.name ||
    participant.identity ||
    'Anonymous';

  return {
    participantId: participant.identity,
    displayName,
    avatarUrl: resolveMetadataValue(metadata, 'avatarUrl'),
    isVR: resolveMetadataFlag(metadata, 'isVR'),
    isHost: resolveMetadataFlag(metadata, 'isHost'),
    isMuted: !isTrackEnabled(participant, Track.Source.Microphone),
    isCameraOn,
    isSharingScreen,
    isLocal,
    audioTrack: audioTrack ?? undefined,
    videoTrack: videoTrack ?? undefined,
  };
}

/**
 * Maps a local participant.
 */
export function mapLocalParticipant(participant: LocalParticipant): ParticipantData {
  return mapLiveKitParticipant(participant, true);
}

/**
 * Maps a remote participant.
 */
export function mapRemoteParticipant(participant: RemoteParticipant): ParticipantData {
  return mapLiveKitParticipant(participant, false);
}

/**
 * Gets a track from a participant by source.
 */
function getTrack(
  participant: Participant,
  source: Track.Source
): MediaStreamTrack | null {
  const publication =
    source === Track.Source.Camera
      ? getCameraPublication(participant)
      : getPublication(participant, source);
  return publication?.track?.mediaStreamTrack ?? null;
}

/**
 * Gets a track publication from a participant.
 */
function getPublication(
  participant: Participant,
  source: Track.Source
): TrackPublication | undefined {
  return participant.getTrackPublication(source);
}

function getCameraTrack(participant: Participant): MediaStreamTrack | null {
  const publication = getCameraPublication(participant);
  return publication?.track?.mediaStreamTrack ?? null;
}

function getCameraPublication(participant: Participant): TrackPublication | undefined {
  for (const publication of participant.trackPublications.values()) {
    if (publication.source !== Track.Source.Camera) {
      continue;
    }

    if (isRoomCameraTrackName(publication.trackName)) {
      continue;
    }

    return publication;
  }

  return undefined;
}

/**
 * Checks if a track source is enabled and subscribed.
 */
function isTrackEnabled(participant: Participant, source: Track.Source): boolean {
  const publication =
    source === Track.Source.Camera
      ? getCameraPublication(participant)
      : getPublication(participant, source);
  if (!publication) {
    return false;
  }

  if (!isParticipantSourceEnabled(participant, source)) {
    return false;
  }

  if (!publication.isSubscribed && !publication.track) {
    return false;
  }

  if (publication.isMuted) {
    return false;
  }

  if (publication.track && 'isMuted' in publication.track && publication.track.isMuted) {
    return false;
  }

  const mediaTrack = publication.track?.mediaStreamTrack;
  if (!isMediaTrackActive(mediaTrack)) {
    return false;
  }

  return true;
}

/**
 * Checks participant-level enabled flags for a track source.
 */
function isParticipantSourceEnabled(
  participant: Participant,
  source: Track.Source
): boolean {
  if (participant instanceof RemoteParticipant) {
    return true;
  }

  const typed = participant as Participant & {
    isCameraEnabled?: boolean;
    isMicrophoneEnabled?: boolean;
    isScreenShareEnabled?: boolean;
  };

  if (source === Track.Source.Camera) {
    return typed.isCameraEnabled !== false;
  }

  if (source === Track.Source.Microphone) {
    return typed.isMicrophoneEnabled !== false;
  }

  if (source === Track.Source.ScreenShare) {
    return typed.isScreenShareEnabled !== false;
  }

  return true;
}

/**
 * Determines whether a MediaStreamTrack is usable for rendering.
 */
function isMediaTrackActive(track?: MediaStreamTrack | null): boolean {
  if (!track) {
    return false;
  }

  if (track.readyState !== 'live') {
    return false;
  }

  if (!track.enabled || track.muted) {
    return false;
  }

  return true;
}

function isRoomCameraTrackName(trackName?: string): boolean {
  if (!trackName) {
    return false;
  }

  return trackName === 'room_camera' || trackName.includes('room_camera');
}

function isVrSelfieTrackName(trackName?: string): boolean {
  if (!trackName) {
    return false;
  }

  return trackName === 'vr_selfie' || trackName.includes('vr_selfie');
}

/**
 * Parses participant metadata safely.
 */
function parseParticipantMetadata(
  participant: Participant
): Record<string, unknown> | null {
  if (!participant.metadata) {
    return null;
  }

  try {
    return JSON.parse(participant.metadata) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Resolves a boolean flag from participant metadata.
 */
function resolveMetadataFlag(
  metadata: Record<string, unknown> | null,
  flag: string
): boolean {
  if (!metadata) {
    return false;
  }

  return parseMetadataBoolean(metadata[flag]) ?? false;
}

function resolveMetadataOptionalFlag(
  metadata: Record<string, unknown> | null,
  flag: string
): boolean | undefined {
  if (!metadata) {
    return undefined;
  }

  return parseMetadataBoolean(metadata[flag]);
}

/**
 * Resolves a string value from participant metadata.
 */
function resolveMetadataValue(
  metadata: Record<string, unknown> | null,
  key: string
): string | undefined {
  if (!metadata) {
    return undefined;
  }

  const value = metadata[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

/**
 * Parses a boolean value from participant metadata.
 */
function parseMetadataBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  return undefined;
}
